// src/App.js
import React, { useRef, useState } from "react";
import { Layout, message, ConfigProvider, theme as antTheme } from "antd";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useLocation,
} from "react-router-dom";
import "./index.css";
import Home from "./pages/Home";
import Resume from "./pages/Resume";
import AboutMe from "./pages/AboutMe";
import Contact from "./pages/Contact";
import Footer from "./components/Footer";
import ThemeToggle from "./components/ThemeToggle";
import { useTheme } from "./hooks/useTheme";

const { Header } = Layout;

const NAV_LINKS = [
  { key: "home", label: "Home", to: "/" },
  { key: "resume", label: "Resume", to: "/resume" },
  { key: "about", label: "About Me", shortLabel: "About", to: "/about" },
];

function App() {
  const footerRef = useRef(null);
  const [footerAttentionKey, setFooterAttentionKey] = useState(0);
  const { isDark, toggleTheme } = useTheme();

  const handleContactClick = () => {
    const el = footerRef.current;
    if (el) {
      const doc = document.documentElement;
      const vh = window.innerHeight;
      const scrollRoom = doc.scrollHeight - vh;
      const alreadyNearBottom = scrollRoom < 140;

      el.scrollIntoView({ behavior: "smooth", block: "nearest" });

      if (alreadyNearBottom) {
        let dismiss = () => {};
        dismiss = message.open({
          type: "info",
          duration: 5,
          className: "max-md:mt-14 contact-bottom-hint-toast",
          content: (
            <div
              className="flex min-w-0 items-center gap-2 pr-0 text-left"
              onClick={() => dismiss()}
            >
              <span className="min-w-0 flex-1 leading-snug">
                You're already at the bottom — the white card is my contact
                info.
              </span>
              <button
                type="button"
                aria-label="Dismiss notification"
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-lg leading-none text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100"
                onClick={(e) => {
                  e.stopPropagation();
                  dismiss();
                }}
              >
                ×
              </button>
            </div>
          ),
        });
      }
    }
    setFooterAttentionKey((k) => k + 1);
  };

  return (
    <ConfigProvider
      theme={{
        algorithm: isDark
          ? antTheme.darkAlgorithm
          : antTheme.defaultAlgorithm,
        token: {
          colorPrimary: "#0891b2",
        },
      }}
    >
      <Router>
        <Layout className="min-h-screen bg-ink-50 dark:bg-ink-950">
          <Header className="site-header relative flex !h-auto min-h-16 items-center !leading-none border-b border-ink-100/80 bg-white/90 py-2 backdrop-blur-md dark:border-ink-800/80 dark:bg-ink-950/90 max-md:!pl-[max(0.75rem,env(safe-area-inset-left,0px))] max-md:!pr-[max(0.75rem,env(safe-area-inset-right,0px))] md:h-16 md:!px-6 md:py-0">
            <div className="flex w-full min-w-0 flex-col gap-2 md:grid md:grid-cols-[1fr_auto_1fr] md:items-center md:gap-4">
              <div className="flex w-full min-w-0 items-center justify-between gap-2 md:contents">
                <Link
                  to="/"
                  className="font-display min-w-0 truncate justify-self-start text-xs font-bold leading-none tracking-wide text-ink-900 transition-colors hover:text-brand-600 dark:text-ink-100 dark:hover:text-brand-400 sm:text-sm md:text-xl"
                >
                  <span className="sm:hidden">JULIAN B.</span>
                  <span className="hidden sm:inline">JULIAN BOYKO</span>
                </Link>

                <div className="shrink-0 md:hidden">
                  <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
                </div>
              </div>

              <NavMenu onContactClick={handleContactClick} />

              <div className="hidden shrink-0 justify-end md:col-start-3 md:flex">
                <ThemeToggle isDark={isDark} onToggle={toggleTheme} />
              </div>
            </div>
          </Header>
          <Layout.Content>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/resume" element={<Resume />} />
              <Route path="/about" element={<AboutMe />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </Layout.Content>
          <Footer ref={footerRef} attentionKey={footerAttentionKey} />
        </Layout>
      </Router>
    </ConfigProvider>
  );
}

function NavMenu({ onContactClick }) {
  const location = useLocation();
  const currentPath = location.pathname;

  const handleContact = (e) => {
    e.preventDefault();
    onContactClick();
  };

  const linkClass = (path) =>
    `nav-link ${currentPath === path ? "nav-link-active" : ""}`;

  return (
    <nav
      className="flex w-full min-w-0 items-center justify-between gap-0 md:col-start-2 md:w-auto md:justify-center md:gap-2"
      aria-label="Main navigation"
    >
      {NAV_LINKS.map(({ key, label, shortLabel, to }) => (
        <Link
          key={key}
          to={to}
          className={linkClass(to)}
        >
          <span className="hidden sm:inline">{label}</span>
          <span className="sm:hidden">{shortLabel || label}</span>
        </Link>
      ))}
      <button
        type="button"
        onClick={handleContact}
        className={`nav-link ${currentPath === "/contact" ? "nav-link-active" : ""}`}
      >
        Contact
      </button>
    </nav>
  );
}

export default App;
